/** Compact query param for abandoned-form / email resume links. */
export const BOOKING_RESUME_PARAM = "bf";

export type BookingResumePrefill = {
  pickup?: string;
  dropoff?: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  dropoffLatitude?: number;
  dropoffLongitude?: number;
  journeyType?: "one_way" | "return" | "wait_return";
  pickupDate?: string;
  pickupTime?: string;
  returnDate?: string;
  returnTime?: string;
  returnPickup?: string;
  returnDropoff?: string;
  returnMirrorDropoff?: boolean;
  flightTracking?: boolean;
  airportPickupType?: "curbside" | "meet_greet";
  flightNo?: string;
  arrivingFrom?: string;
  terminal?: string;
  adults?: number;
  children?: number;
  luggage?: number;
  childSeat?: boolean;
  wheelchair?: boolean;
  assistanceDog?: boolean;
  notes?: string;
  paymentMethod?: string;
  firstName?: string;
  surname?: string;
  mobileCountryCode?: string;
  mobile?: string;
  email?: string;
  selectedVehicleId?: string;
  selectedVehicleName?: string;
  couponCode?: string;
};

export const BOOKING_SOURCE_SITE_URLS: Record<string, string> = {
  "southampton-port-taxi": "https://www.southamptonporttaxi.co.uk",
  "airport-transfers-taxi": "https://www.airporttransferstaxi.co.uk",
  "uk-airport-taxi": "https://www.ukairporttaxitransfers.co.uk",
  "liverpool-dock-taxi": "https://www.liverpooldockstaxi.co.uk",
  "tikla-cars": "https://tiklacars.co.uk",
};

export const BOOKING_SOURCE_SITE_NAMES: Record<string, string> = {
  "southampton-port-taxi": "Southampton Port Taxi",
  "airport-transfers-taxi": "Airport Transfers Taxi",
  "uk-airport-taxi": "UK Airport Taxi",
  "liverpool-dock-taxi": "Liverpool Docks Taxi",
  "tikla-cars": "Tikla Cars",
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function asOptionalString(value: unknown): string | undefined {
  return isNonEmptyString(value) ? value.trim() : undefined;
}

function asOptionalNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function asOptionalBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

function encodeBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function sanitizeBookingPrefill(
  input: Record<string, unknown> | BookingResumePrefill | null | undefined,
): BookingResumePrefill {
  if (!input) return {};
  const out: BookingResumePrefill = {};
  const record = input as Record<string, unknown>;

  const strKeys: (keyof BookingResumePrefill)[] = [
    "pickup",
    "dropoff",
    "pickupDate",
    "pickupTime",
    "returnDate",
    "returnTime",
    "returnPickup",
    "returnDropoff",
    "flightNo",
    "arrivingFrom",
    "terminal",
    "notes",
    "paymentMethod",
    "firstName",
    "surname",
    "mobileCountryCode",
    "mobile",
    "email",
    "selectedVehicleId",
    "selectedVehicleName",
    "couponCode",
  ];
  for (const key of strKeys) {
    const value = asOptionalString(record[key]);
    if (value) (out as Record<string, unknown>)[key] = value;
  }

  const numKeys: (keyof BookingResumePrefill)[] = [
    "pickupLatitude",
    "pickupLongitude",
    "dropoffLatitude",
    "dropoffLongitude",
    "adults",
    "children",
    "luggage",
  ];
  for (const key of numKeys) {
    const value = asOptionalNumber(record[key]);
    if (value != null) (out as Record<string, unknown>)[key] = value;
  }

  const boolKeys: (keyof BookingResumePrefill)[] = [
    "returnMirrorDropoff",
    "flightTracking",
    "childSeat",
    "wheelchair",
    "assistanceDog",
  ];
  for (const key of boolKeys) {
    const value = asOptionalBoolean(record[key]);
    if (value != null) (out as Record<string, unknown>)[key] = value;
  }

  const journeyType = asOptionalString(record.journeyType);
  if (journeyType === "one_way" || journeyType === "return" || journeyType === "wait_return") {
    out.journeyType = journeyType;
  }

  const airportPickupType = asOptionalString(record.airportPickupType);
  if (airportPickupType === "curbside" || airportPickupType === "meet_greet") {
    out.airportPickupType = airportPickupType;
  }

  return out;
}

export function progressToBookingPrefill(
  progress: Record<string, unknown>,
): BookingResumePrefill {
  return sanitizeBookingPrefill(progress);
}

export function encodeBookingResume(prefill: BookingResumePrefill): string | null {
  const clean = sanitizeBookingPrefill(prefill);
  if (Object.keys(clean).length === 0) return null;
  return encodeBase64Url(JSON.stringify(clean));
}

export function buildBookingResumeUrl(
  siteBaseUrl: string,
  prefill: BookingResumePrefill,
  path = "/",
): string | null {
  const token = encodeBookingResume(prefill);
  if (!token) return null;
  const base = siteBaseUrl.replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${base}${normalizedPath}`);
  url.searchParams.set(BOOKING_RESUME_PARAM, token);
  if (normalizedPath === "/" || normalizedPath === "") {
    url.hash = "book";
  }
  return url.toString();
}

export function buildAbandonedSessionResumeUrl(
  bookingSource: string,
  progress: Record<string, unknown>,
): string | null {
  const siteUrl = BOOKING_SOURCE_SITE_URLS[bookingSource];
  if (!siteUrl) return null;
  const prefill = progressToBookingPrefill(progress);
  const path = bookingSource === "tikla-cars" ? "/booking" : "/";
  return buildBookingResumeUrl(siteUrl, prefill, path);
}

export function buildAbandonedBookingMailto(options: {
  to?: string | null;
  siteName: string;
  resumeUrl: string;
  routeSummary?: string;
}): string {
  const to = options.to?.trim() || "";
  const subject = encodeURIComponent(`Complete your ${options.siteName} booking`);
  const routeLine = options.routeSummary
    ? `Journey: ${options.routeSummary}\n\n`
    : "";
  const body = encodeURIComponent(
    `Hi,\n\nYou started a booking with ${options.siteName} but did not finish.\n\n${routeLine}Continue where you left off here:\n${options.resumeUrl}\n\nIf you have any questions, just reply to this email.\n\nThanks,\n${options.siteName}`,
  );
  return to
    ? `mailto:${encodeURIComponent(to)}?subject=${subject}&body=${body}`
    : `mailto:?subject=${subject}&body=${body}`;
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}
